import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'

import HeaderGroup from './HeaderGroup'
import HeaderCell from './HeaderCell'

import './style.scss'

const Header = ({
  header,
  headerWithChildren,
  onHandleSort,
  onHandleExpandAll,
  collapsable,
  expandedAll,
  sortable,
  syncDimensions,
  autoHeaderCellWidth,
  extraClasses
}) => {
  const targetCollapseRef = useRef()
  const [headerColumns, setHeaderColumns] = useState(header)

  const [isExpandedAll, setExpandedAllStatus] = useState()

  const onExpand = (e) => {
    e.stopPropagation()
    onHandleExpandAll(!isExpandedAll)
    setExpandedAllStatus(!isExpandedAll)
  }

  useEffect(() => {
    setExpandedAllStatus(expandedAll)
  }, [expandedAll])

  const handleSort = (cell) => {
    let newCell = {}
    if (!cell.sortOrder) {
      newCell = {
        ...cell,
        sortOrder: cell.type === 'number' ? 'desc' : 'asc'
      }
    } else {
      newCell = {
        ...cell,
        sortOrder: cell.sortOrder === 'asc' ? 'desc' : 'asc'
      }
    }

    const modifyHeaderColumns = (allColumns) => {
      const formattedColumns = allColumns.map((column) => {
        let newColumn = {}

        const subColumns = column.children
        if (subColumns && subColumns.length > 0) {
          const newChildren = modifyHeaderColumns(subColumns)
          newColumn = {
            ...column,
            children: newChildren
          }

          return newColumn
        }

        newColumn = column.sortable ? (column.key === newCell.key ? newCell : { ...column, sortOrder: '' }) : column

        return newColumn
      })

      return formattedColumns
    }

    const newHeaderColumns = modifyHeaderColumns(headerColumns)

    setHeaderColumns(newHeaderColumns)
    onHandleSort(newCell)
  }

  const tempHeaderDimensions = []
  const headerTitleArray = []
  const handleHeaderDimensions = ({ dimension, cell }) => {
    const calculatedDimension =
      targetCollapseRef.current && tempHeaderDimensions.length === 0
        ? {
            isHighPriority: true,
            width: `${parseFloat(dimension.width) + targetCollapseRef.current.offsetLeft}px`
          }
        : dimension
    tempHeaderDimensions.push(calculatedDimension)

    _.remove(headerTitleArray, (key) => key === cell.key)

    if (headerTitleArray.length === 0) {
      syncDimensions(tempHeaderDimensions)
    }
  }

  const getRowCells = (headerData, rowCellIndex = 0) =>
    headerData.map((headerCell, headerCellIndex) => {
      const defaultHeaderStyle = headerCell.style || {}
      const headerCellStyle = autoHeaderCellWidth ? { width: autoHeaderCellWidth, ...defaultHeaderStyle } : defaultHeaderStyle
      if (headerCell.children) {
        return (
          <HeaderGroup key={`fil-table--header-group-${headerCellIndex}`} header={headerCell}>
            {(children) => getRowCells(children, headerCellIndex)}
          </HeaderGroup>
        )
      }

      headerTitleArray.push(headerCell.key)
      return (
        <HeaderCell
          key={`fil-table--header-cell-${headerCellIndex}`}
          syncDimensions={handleHeaderDimensions}
          sortable={sortable}
          onHandleSort={handleSort}
          onHandleExpandAll={onHandleExpandAll}
          expandedAll={expandedAll}
          style={{ ...headerCellStyle }}
          cell={headerCell}
          cellIndex={`${rowCellIndex}${headerCellIndex}`}
        />
      )
    })

  useEffect(() => {
    setHeaderColumns(header)
  }, [header])

  return (
    <div className={`fil-table--header ${extraClasses}`}>
      {collapsable && (
        <span className="fil-table--header-expand-icon" onClick={onExpand} ref={targetCollapseRef}>
          <fil-icon size="16" icon={`custom-chevron-${isExpandedAll ? 'up' : 'down'}`} />
        </span>
      )}
      <div
        className={classnames('fil-table--header-row', {
          'fil-table--header-row-with-children': headerWithChildren
        })}
      >
        {getRowCells(headerColumns)}
      </div>
    </div>
  )
}

Header.propTypes = {
  header: PropTypes.array.isRequired,
  headerWithChildren: PropTypes.bool,
  onHandleSort: PropTypes.func,
  onHandleExpandAll: PropTypes.func,
  sortable: PropTypes.bool,
  collapsable: PropTypes.bool,
  expandedAll: PropTypes.bool,
  syncDimensions: PropTypes.func,
  autoHeaderCellWidth: PropTypes.string,
  extraClasses: PropTypes.string
}

Header.defaultProps = {
  headerWithChildren: false,
  onHandleSort: () => {},
  onHandleExpandAll: () => {},
  syncDimensions: () => {},
  sortable: false,
  collapsable: false,
  expandedAll: true,
  autoHeaderCellWidth: '',
  extraClasses: ''
}

export default Header
